import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownRight, AlertTriangle, Loader2 } from "lucide-react";
import { useMode } from "../context/ModeContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { usd, clsx } from "../lib/format";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const DURATIONS = [
  { label: "5s", v: 5 }, { label: "10s", v: 10 }, { label: "15s", v: 15 }, { label: "30s", v: 30 },
  { label: "1m", v: 60 }, { label: "2m", v: 120 }, { label: "5m", v: 300 },
];

export default function TradePanel({ symbol, price, returnRate = 0.85, botId = null, onTraded }) {
  const { mode, balance, refreshAccount } = useMode();
  const [amount, setAmount] = useState(100);
  const [duration, setDuration] = useState(30);
  const [placing, setPlacing] = useState(false);
  const [flash, setFlash] = useState(null);
  const [stopLoss, setStopLoss] = useState("");
  const [takeProfit, setTakeProfit] = useState("");
  const isLive = mode === "live";

  const expected = (Number(amount) || 0) * returnRate;

  const place = async (direction) => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (amt > balance) { toast.error(`Insufficient ${isLive ? "live" : "demo"} balance`); return; }
    setPlacing(true);
    setFlash(direction);
    setTimeout(() => setFlash(null), 700);
    try {
      await api.post("/trades", { symbol, direction, amount: amt, duration, bot_id: botId, stop_loss: stopLoss ? Number(stopLoss) : null, take_profit: takeProfit ? Number(takeProfit) : null });
      toast.success(`${direction.toUpperCase()} placed · ${usd(amt)} on ${symbol}`);
      await refreshAccount();
      onTraded?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="flex flex-col h-full p-4 gap-4">
      <div className={clsx("rounded-lg px-3 py-2 text-center border", isLive ? "bg-gold/10 border-gold/30" : "bg-brandblue/10 border-brandblue/30")}>
        <div className={clsx("text-[10px] font-mono uppercase tracking-widest", isLive ? "text-gold" : "text-brandblue")}>{isLive ? "Live Account" : "Demo Account"}</div>
        <div className="font-mono font-bold text-lg text-white">{usd(balance)}</div>
      </div>

      <div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-fx-s3">Asset</span>
          <span className="font-semibold text-white">{symbol}</span>
        </div>
        <div className="flex items-center justify-between text-sm mt-1">
          <span className="text-fx-s3">Price</span>
          <span className="font-mono text-white">{price ? (price < 10 ? price.toFixed(5) : price.toLocaleString()) : "-"}</span>
        </div>
        <div className="flex items-center justify-between text-sm mt-1">
          <span className="text-fx-s3">Payout</span>
          <span className="font-mono text-pos">{Math.round(returnRate * 100)}%</span>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-xs text-fx-s3 uppercase tracking-wider">Stake</label>
        <Input data-testid="trade-amount-input" type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11 bg-fx-base border-white/10 font-mono text-lg" />
        <div className="flex gap-2">
          {[10, 50, 100, 500].map((v) => (
            <button key={v} onClick={() => setAmount(v)} data-testid={`amount-preset-${v}`} className="flex-1 py-1.5 rounded-md bg-fx-s2 text-xs font-mono text-fx-s3 hover:text-white hover:bg-fx-s3 transition-colors">${v}</button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-xs text-fx-s3 uppercase tracking-wider">Duration</label>
        <div className="grid grid-cols-4 gap-2">
          {DURATIONS.map((d) => (
            <button key={d.v} onClick={() => setDuration(d.v)} data-testid={`duration-${d.v}`}
              className={clsx("py-2 rounded-md text-xs font-mono transition-colors", duration === d.v ? "bg-brandblue text-white" : "bg-fx-s2 text-fx-s3 hover:text-white")}>
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg bg-fx-base border border-white/5 p-3 flex items-center justify-between">
        <span className="text-sm text-fx-s3">Expected return</span>
        <span className="font-mono font-bold text-pos">+{usd(expected)}</span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[11px] text-fx-s3 flex items-center gap-1 mb-1">Stop Loss <span className="text-neg">▾</span></label>
          <Input data-testid="trade-sl-input" type="number" placeholder="price" value={stopLoss} onChange={(e) => setStopLoss(e.target.value)} className="h-9 bg-fx-base border-white/10 font-mono text-sm" />
        </div>
        <div>
          <label className="text-[11px] text-fx-s3 flex items-center gap-1 mb-1">Take Profit <span className="text-pos">▴</span></label>
          <Input data-testid="trade-tp-input" type="number" placeholder="price" value={takeProfit} onChange={(e) => setTakeProfit(e.target.value)} className="h-9 bg-fx-base border-white/10 font-mono text-sm" />
        </div>
      </div>

      {isLive && (
        <div className="rounded-lg bg-gold/10 border border-gold/30 p-3 flex items-start gap-2" data-testid="live-internal-banner">
          <AlertTriangle className="h-4 w-4 text-gold shrink-0 mt-0.5" />
          <p className="text-xs text-gold/90 leading-relaxed font-medium">Live trades execute on internal engine using your live wallet balance. Real funds are at risk.</p>
        </div>
      )}

      <div className="mt-auto grid grid-cols-2 gap-3">
        <Button data-testid="trade-call-button" disabled={placing} onClick={() => place("buy")}
          className={clsx("h-16 lg:h-14 flex-col gap-0 text-base bg-pos hover:bg-emerald-500 text-white font-bold disabled:opacity-40", flash === "buy" && "flash-green")}>
          {placing && flash === "buy" ? <Loader2 className="h-5 w-5 animate-spin" /> : <><ArrowUpRight className="h-6 w-6" /><span>BUY</span></>}
        </Button>
        <Button data-testid="trade-put-button" disabled={placing} onClick={() => place("sell")}
          className={clsx("h-16 lg:h-14 flex-col gap-0 text-base bg-neg hover:bg-red-500 text-white font-bold disabled:opacity-40", flash === "sell" && "flash-red")}>
          {placing && flash === "sell" ? <Loader2 className="h-5 w-5 animate-spin" /> : <><ArrowDownRight className="h-6 w-6" /><span>SELL</span></>}
        </Button>
      </div>
    </div>
  );
}
